import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import Panel from "../../panels/Panel";
import Table from "../../Table";
import ExpandButton from "../../graphs/ExpandButton";
import UsdsBoxPlotChart from "./UsdsBoxPlotChart";
import ShowDataButton from "./ShowDataButton";
import UsdsChartLegend, {
  benchmarkLegendItems,
  landmarkLegendItems,
} from "./UsdsChartLegend";
import {
  FALLBACK_COLORS,
  REACH_COLORS,
  boxesDomain,
  computeYDomain,
} from "./usdsBoxPlotHelpers";
import { periodLabel } from "../../../pages/upstreamDownstream/usdsParams";

/**
 * A single reach panel: the box plot, its footnotes, the export and expand
 * controls and a raw table that doubles as the accessible fallback.
 */
export default function UsdsBoxPlotChartContainer({
  reach,
  parameter,
  selectedLabel,
  domain,
  landmarks = [],
  scale = "linear",
  height = 420,
}) {
  /* Declared inside the component, as every page here does: a module-level
     array is one shared object material-table would scribble on for the life
     of the tab (see CLAUDE.md). Copies are still handed over at render. */
  const TABLE_COLUMNS = [
    { title: "Site", field: "site" },
    { title: "Period", field: "period" },
    { title: "Site coverage", field: "site_coverage" },
    { title: "Date", field: "collect_date" },
    { title: "Value", field: "result_value", type: "numeric" },
    { title: "Units", field: "unit" },
    { title: "Non-detect", field: "nondetect" },
    { title: "Outlier", field: "is_outlier" },
    { title: "n", field: "recordcount", type: "numeric" },
    { title: "Median", field: "median", type: "numeric" },
    { title: "25th", field: "pctl25", type: "numeric" },
    { title: "75th", field: "pctl75", type: "numeric" },
    { title: "Synoptic site", field: "synoptic" },
  ];

  const [expanded, setExpanded] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());

  const toggle = (label) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  const chartHeight = expanded ? Math.round(height * 1.8) : height;
  const synoptic = reach.synopticSites ?? [];
  /* Unlocked, the axis follows whatever is still shown, so hiding the wide
     comparison box zooms in on the selected year. Locked, the shared
     cross-reach scale wins, since comparing reaches is what the lock is for. */
  const visibleBoxes = useMemo(
    () =>
      reach.boxes.filter(
        (b) =>
          !hidden.has(
            periodLabel(b.collectYear, b.waterYearMin, b.waterYearMax)
          )
      ),
    [reach.boxes, hidden]
  );
  const axisDomain = domain ?? boxesDomain(visibleBoxes) ?? reach.dataDomain;
  /* Hiding the benchmark from the legend also takes it out of the axis, so the
     scale settles back onto the data. */
  const hideBenchmark = benchmarkLegendItems(parameter).some((b) =>
    hidden.has(b.label)
  );
  const { hiddenBenchmarks } = computeYDomain(
    axisDomain,
    hideBenchmark ? null : parameter
  );
  const [dark, light] = REACH_COLORS[reach.reachGroupid] ?? FALLBACK_COLORS;
  /* Colors belong to the period, not to its position, so hiding the selected
     year must not turn the comparison dark. */
  const periodColors = useMemo(
    () =>
      new Map(
        reach.subGroups.map((label, i) => [label, i === 0 ? dark : light])
      ),
    [reach.subGroups, dark, light]
  );
  const benchmarkItems = benchmarkLegendItems(parameter, hiddenBenchmarks);
  const legendItems = [
    ...reach.subGroups.map((label) => ({
      kind: "box",
      color: periodColors.get(label),
      label,
    })),
    ...benchmarkItems,
    ...landmarkLegendItems(landmarks),
  ];

  /* One row per sample, carrying the statistics of the box it belongs to, so
     an export is self-describing. Same shared table as the other graph pages:
     sorting, search, column picker, copy and export. */
  const tableRows = useMemo(
    () =>
      reach.boxes.flatMap((box) =>
        box.values.map((v) => ({
          site: box.locationName,
          period: box.period,
          site_coverage: `WY ${box.waterYearMin} to ${box.waterYearMax}`,
          collect_date: v.collectDate,
          result_value: v.resultValue,
          unit: parameter?.unitName ?? "",
          nondetect: v.nondetect ? "Yes" : "No",
          is_outlier: v.isOutlier ? "Yes" : "No",
          recordcount: box.recordcount,
          median: box.median,
          pctl25: box.pctl25,
          pctl75: box.pctl75,
          synoptic: box.isSynoptic ? "Yes" : "No",
        }))
      ),
    [reach.boxes, parameter]
  );

  const visibleReach = useMemo(() => {
    const subGroups = reach.subGroups.filter((g) => !hidden.has(g));
    return {
      ...reach,
      subGroups,
      values: reach.values.filter((v) => !hidden.has(v.period)),
    };
  }, [reach, hidden]);

  return (
    <Panel
      title={reach.reachLabel}
      rightHeader={
        <>
          <ShowDataButton
            open={showTable}
            onClick={() => setShowTable((v) => !v)}
          />
          <ExpandButton
            handleExpand={() => setExpanded((v) => !v)}
            expanded={expanded}
          />
        </>
      }
    >
      <div>
        <UsdsChartLegend
          items={legendItems}
          hidden={hidden}
          onToggle={toggle}
        />
        <UsdsBoxPlotChart
          reach={visibleReach}
          parameter={parameter}
          selectedLabel={selectedLabel}
          domain={axisDomain}
          periodColors={periodColors}
          hideBenchmark={hideBenchmark}
          landmarks={landmarks}
          scale={scale}
          height={chartHeight}
        />
      </div>

      {reach.boxes.some((b) => b.mostlyNondetect) && (
        <Typography variant="caption" color="textSecondary" component="p">
          Boxes marked ND are mostly non-detects, which are stored and plotted
          at zero; they describe the detection limit more than the stream.
        </Typography>
      )}

      {synoptic.length > 0 && (
        <Typography variant="caption" color="textSecondary" component="p">
          {synoptic.join(", ")} {synoptic.length === 1 ? "is" : "are"} sampled
          two or three times a water year, so a single-year box there rests on
          very few points. The comparison box carries the distribution.
        </Typography>
      )}

      {showTable && (
        <Box mt={2}>
          {/* material-table writes its own `tableData` onto every column and
              row it is handed, so a persistent array accumulates those
              mutations across renders until the tab locks up (see main,
              4313d33). Fresh shallow copies each render keep them off our
              objects; the shared columns constant must never reach it. */}
          <Table
            label={`${reach.reachLabel} ${parameter?.paramDescrip ?? ""}`}
            data={tableRows.map((row) => ({ ...row }))}
            height={360}
            columns={TABLE_COLUMNS.map((column) => ({ ...column }))}
          />
        </Box>
      )}
    </Panel>
  );
}

/** Shown while the reach list is still loading. */
export function UsdsBoxPlotLoading({ height = 420 }) {
  return (
    <Box
      height={height}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress />
    </Box>
  );
}
